import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import styles from './page.module.css';
import AppBar from '../../../components/AppBar';
import { useTheme } from '../../../context/ThemeProvider';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import ContentGrid from '../../../components/ContentGrid';
import { GlobalMicroButton } from '../../../components/GlobalMicroButton';
import AppFooter from '../../../components/AppFooter';
import { useUser } from '../../../context/UserContext';
import { useToast } from '../../../context/ToastProvider';
import { useKnowledge } from '../../../hooks/useKnowledge';
import { Knowledge } from '../../../hooks/useKnowledge';
import { KnowledgeDocumentsTab } from '../../../components/KnowledgeDocumentsTab';
import AddKnowledgeDocumentSidebar from '../../../components/AddKnowledgeDocumentSidebar';
import EditKnowledgeSidebar from '../../../components/EditKnowledgeSidebar';
import DoubleConfirmationModal from '../../../components/DoubleConfirmationModal';
import { db } from '../../../lib/firebase';

const BATCH_SIZE = 500;

async function deleteKnowledgeWithSubcollections(knowledgeId: string): Promise<void> {
  const documentsRef = collection(db, 'knowledge', knowledgeId, 'documents');
  const chunksRef = collection(db, 'knowledge', knowledgeId, 'documentKnowledge');

  const deleteCollection = async (ref: ReturnType<typeof collection>) => {
    const snapshot = await getDocs(ref);
    let batch = writeBatch(db);
    let count = 0;
    for (const docSnap of snapshot.docs) {
      batch.delete(docSnap.ref);
      count++;
      if (count >= BATCH_SIZE) {
        await batch.commit();
        batch = writeBatch(db);
        count = 0;
      }
    }
    if (count > 0) await batch.commit();
  };

  await deleteCollection(documentsRef);
  await deleteCollection(chunksRef);
  await deleteDoc(doc(db, 'knowledge', knowledgeId));
}

export default function KnowledgePage() {
  const { knowledgeId } = useParams<{ knowledgeId: string }>();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const { role, company } = useUser();
  // Internal admin may not have company set; use Thescon internal company as fallback
  const INTERNAL_ADMIN_COMPANY_ID = 'OKGCziNDxHTksvd9Iei5';
  const effectiveCompanyId = company || (role === 'internal_admin' ? INTERNAL_ADMIN_COMPANY_ID : '');
  const showToast = useToast();
  const { getKnowledgeById, updateKnowledge } = useKnowledge(false);

  const [knowledge, setKnowledge] = useState<Knowledge | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddDocumentSidebar, setShowAddDocumentSidebar] = useState(false);
  const [showEditSidebar, setShowEditSidebar] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Only redirect once role has loaded and is not internal_admin (avoid redirect on refresh when role is still '')
    if (role && role !== 'internal_admin') {
      navigate('/home');
    }
  }, [role, navigate]);

  useEffect(() => {
    const fetchKnowledge = async () => {
      if (!knowledgeId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getKnowledgeById(knowledgeId);
        setKnowledge(data);
      } catch {
        setKnowledge(null);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledge();
  }, [knowledgeId, getKnowledgeById]);

  const handleEditSave = async (id: string, data: { knowledgeName: string; knowledgeDescription: string }) => {
    await updateKnowledge(id, data);
    setKnowledge((prev) => (prev && prev.id === id ? { ...prev, ...data } : prev));
  };

  const handleDeleteConfirm = async () => {
    if (!knowledgeId) return;
    setIsDeleting(true);
    try {
      await deleteKnowledgeWithSubcollections(knowledgeId);
      showToast(t('knowledge_delete_success') || 'Collection deleted successfully', 'success');
      navigate('/home?tab=knowledge');
    } catch (error) {
      console.error('Error deleting knowledge:', error);
      showToast(t('knowledge_delete_error') || 'Failed to delete collection', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%'
      }}>
        <img
          src={isDarkMode ? "/assets/graphics/thescon-loading-white.svg" : "/assets/graphics/thescon-loading.svg"}
          alt="Loading"
          width={120}
          height={120}
        />
      </div>
    );
  }

  if (!knowledge) {
    return (
      <div className={styles.container}>
        <AppBar />
        <ContentGrid rows="64px 136px 1fr 48px">
          <div className={styles.loadingContainer}>
            <p>{t('knowledge_page_not_found') || 'Knowledge not found'}</p>
          </div>
        </ContentGrid>
      </div>
    );
  }

  return (
    <div>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid
        mobileRows="32px 1fr auto 48px"
        tabletRows="64px 1fr 48px"
        rows="64px 1fr 48px"
      >
        {/* Toolbar left side */}
        <div className={styles.homeToolbarLeft}>

          {/* Back button */}
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('knowledge_page_navigate_back') || 'Back'}
              icon={<img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt="Left" width={8} height={8} />}
              onClick={() => navigate('/home?tab=knowledge')}
            />
          </div>

          {/* Title */}
          <b>{t('knowledge_page_title') || 'Knowledge'}</b>
        </div>

        {/* Toolbar right side */}
        <div className={styles.homeToolbarRight}>
          <GlobalMicroButton
            title={t('knowledge_page_edit') || 'Edit collection'}
            icon={<img src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'} alt="" width={16} height={16} />}
            onClick={() => setShowEditSidebar(true)}
          >
            {t('knowledge_page_edit') || 'Sammlung bearbeiten'}
          </GlobalMicroButton>
          <GlobalMicroButton
            title={t('knowledge_page_delete') || 'Delete collection'}
            icon={<img src="/assets/icons/trash.red.svg" alt="" width={16} height={16} />}
            onClick={() => setShowDeleteModal(true)}
            destructive
          >
            {t('knowledge_page_delete') || 'Sammlung löschen'}
          </GlobalMicroButton>
        </div>

        {/* Documents content */}
        <div className={styles.mainContent}>

          <div className={styles.knowledgeHeader}>

            {/* Title */}
            <h1>{knowledge.knowledgeName || t('knowledge_page_title') || 'Knowledge'}</h1>

            {/* Description */}
            <p>{knowledge.knowledgeDescription || ''}</p>
          </div>

          {/* Documents tab */}
          {knowledgeId && (
            <KnowledgeDocumentsTab
              knowledgeId={knowledgeId}
              onAddDocument={() => setShowAddDocumentSidebar(true)}
            />
          )}
        </div>
      </ContentGrid>

      {/* Add document sidebar */}
      <AddKnowledgeDocumentSidebar
        isOpen={showAddDocumentSidebar}
        onClose={() => setShowAddDocumentSidebar(false)}
        knowledgeId={knowledgeId || ''}
        companyId={effectiveCompanyId}
        onSuccess={() => setShowAddDocumentSidebar(false)}
      />

      {/* Edit collection sidebar */}
      <EditKnowledgeSidebar
        isOpen={showEditSidebar}
        onClose={() => setShowEditSidebar(false)}
        knowledge={knowledge}
        onSave={handleEditSave}
        onSuccess={() => setShowEditSidebar(false)}
      />

      {/* Delete confirmation modal */}
      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('knowledge_delete_confirm_title') || `Delete "${knowledge?.knowledgeName || t('knowledge_page_title')}"?`}
        paragraph={t('knowledge_delete_confirm_paragraph') || 'This action cannot be undone. The entire knowledge collection will be permanently deleted, including all documents and their chunks. This is irreversible.'}
        primaryLabel={t('knowledge_delete_confirm_button') || 'Delete collection'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('project_page_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={handleDeleteConfirm}
        onSecondary={() => setShowDeleteModal(false)}
        primaryDestructive
        loading={isDeleting}
      />

      {/* Footer */}
      <AppFooter inline />
    </div>
  );
}
